import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import axios, { AxiosRequestConfig } from 'axios';
import _ from 'lodash';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { createContainer } from 'unstated-next';
import * as yup from 'yup';

import { datesHaveBeenUpdated, groupSessions, returnClassDataDelta, returnMeetingDataDelta } from './classPayloadUtils';
import { CLASS_CREATE_ZOOM_PASSWORD_ERROR, getClassErrorMessage } from './error-codes';
import {
    CanJoinBeforeInstructor,
    ClassCreatePayload,
    ClassDataUpdate,
    ClassSessionGroupPayload,
    ClassUpdatePayload,
    InitialClassFormValues,
    MeetingObj,
    RequireAuth,
    TemplateRecord,
    WaitingRoom
} from './types';
import { returnClassOptions } from './utils';
import { ZoomMeetingSettings } from '../../../../models/types';
import { ClassSession, ClassSessionWithGroupData } from '../../components/addSessions/types';
import { useCreateCalendarValues } from '../../components/addSessions/useCreateCalendarValues';
import { aggregateCalendarDates } from '../../components/addSessions/utils/aggregateCalendarDates';
import {
    calcSessionsFromClassTimes,
    calcSessionsFromDates,
    filterSessions
} from '../../components/addSessions/utils/calcSessions';
import { ClassRecording } from '../../components/classroom/types';
import {
    AUTH_ME,
    CLASS_CREATE,
    CLASS_PUBLISH,
    GET_CLASS_DATA_PATH,
    TEMPLATES_GET,
    UPDATE_CLASS
} from '../../config/apiPaths';
import { ClassRecord, ClassSessionGroup, ClassStatus, ClassStudentGroup } from '../../context/types';
import { useAppMessaging } from '../../hooks/useAppMessaging';
import { useOrgSettings } from '../../hooks/useOrgSettings';
import {
    ClassJoinPermission,
    OverridableClassOptions,
    ScreenShareResolutions,
    UnauthUserAccess,
    WebExpOptions
} from '../../types/adminConfig';
import { ClassData, LeadInstructor, LmsSyncSettings, ZoomMeetingData } from '../../types/apiTypes';
import { getUserUuidHeaders } from '../../utils/httpUtils';
import { isValidEmail } from '../../utils/inputValidation';
import { PasswordGenerator } from '../../utils/passwordGenerator';
import { getUrlParams, getUrls, processApiPath } from '../../utils/url';
import { usePrevious } from '../../utils/usePrevious';

type ClassRequestCallbacks = { onSuccess?: () => void; onError?: (message: string) => void };

type LoadingStatus = 'INITIAL' | 'LOADING' | 'LOADING_MORE' | 'SUCCESS' | 'ERROR';

type FormActionType = 'CREATE' | 'EDIT';

interface ClassContainerState {
    classData?: ClassData; // result of the GET_CLASS_DATA_PATH api call
}

const STATIC_DEFAULT_VALUES: InitialClassFormValues = {
    className: '',
    classId: '',
    description: '',
    classPasscode: '',
    courseTemplate: false,
    courseTemplateId: '',
    dates: [],
    waitingRoom: WaitingRoom.OFF,
    canJoinBeforeInstructor: CanJoinBeforeInstructor.YES,
    requireAuth: RequireAuth.OFF,
    instructorEmails: [],
    instructor: '',
    additionalInstructors: [],
    contentType: 'default',
    ltiState: {},
    lmsSyncSettings: {},
    classSessions: [],
    classSessionGroups: [],
    classGroups: [],
    classOptions: {},
    webExperience: WebExpOptions.NextGen, // updated when orgSettings is available
    allowVanillaZoom: false, // updated when orgSettings is available
    unauthUserAccess: UnauthUserAccess.Allow, // updated when orgSettings is available
    unauthUserText: '', // updated when orgSettings is available
    status: 'published',
    classJoinPermission: ClassJoinPermission.All,
    rosterSelfEnrollment: false,
    attendanceGradeTardyScore: 80,
    hasAiTa: false,
    hasFileAnnotations: false,
    hasPresentations: true,
    allowNextGenMobile: true,
    enablePrivateDiscussion: false,
    orgScreenshareRes: ScreenShareResolutions.High
};

const useClassContainer = (state: ClassContainerState = {}) => {
    const { api } = getUrls();

    const { sendMessage, closeWindow } = useAppMessaging();

    const { classUuid: classUuidV1 } = useParams<{ classUuid?: string }>();
    const { class_uuid: classUuidV2 } = getUrlParams();
    const classUuid = classUuidV1 || classUuidV2;
    const [formActionType] = useState<FormActionType>(classUuid ? 'EDIT' : 'CREATE');
    const [initialClassTimes, setInitialClassTimes] = useState<string>('');
    const [classRecord, setClassRecord] = useState<ClassRecord>();
    const [initialData, setInitialData] = useState<InitialClassFormValues>();
    const [meetingSettings, setMeetingSettings] = useState<Partial<ZoomMeetingSettings>>();
    const [ltiState, setLtiState] = useState<any>({});
    const [lmsSyncSettings, setLmsSyncSettings] = useState<LmsSyncSettings>({});
    const [hostEmail, setHostEmail] = useState<string>('');
    const [leadInstructor, setLeadInstructor] = useState<LeadInstructor | null>(null);

    const [classNameHasBeenSet, setClassNameHasBeenSet] = useState(false);

    const [classGroups, setClassGroups] = useState<ClassStudentGroup[]>([]);
    const [originalClassSessionGroups, setOriginalClassSessionGroups] = useState<ClassSessionGroup[]>([]);

    const [createClassStatus, setCreateClassStatus] = useState<LoadingStatus>('INITIAL');
    const [updateClassStatus, setUpdateClassStatus] = useState<LoadingStatus>('INITIAL');
    const [getClassToEditStatus, setGetClassToEditStatus] = useState<LoadingStatus>('INITIAL');
    const [publishedTemplates, setPublishedTemplates] = useState<TemplateRecord[]>([]);
    const [errorMessage, setErrorMessage] = useState<string>('');

    const [recordings, setRecordings] = useState<ClassRecording[]>([]);

    const { data: orgSettings } = useOrgSettings();

    const allowForcedAuthentication =
        orgSettings?.zoomAppId !== 'teams' &&
        (orgSettings?.authSettings.forcedAuthentication || meetingSettings?.meeting_authentication);

    const userUuid = window.localStorage.getItem('user_uuid') as string;

    const { t } = useTranslation('create_edit_class');

    const {
        schema: calendarSchema,
        defaultValues: defaultCalendarValues,
        defaultValuesWithInitialDay,
        returnNewDefaultCalendarValues
    } = useCreateCalendarValues();

    const schema = yup.object({
        className: yup.string().required(t('details.class-name-required')).max(200, t('details.class-name-max-char')),
        classId: yup.string().optional(),
        description: yup.string().optional().max(2000, t('details.class-description-max-char')),
        classPasscode: yup.string().required(t('details.class-passcode-required')),
        courseTemplate: yup.boolean().optional(),
        courseTemplateId: yup.string().optional(),
        waitingRoom: yup.mixed<WaitingRoom>().oneOf(Object.values(WaitingRoom)),
        attendanceGradeTardyScore: yup.number().min(0).max(100).optional(),
        canJoinBeforeInstructor: yup.mixed<CanJoinBeforeInstructor>().oneOf(Object.values(CanJoinBeforeInstructor)),
        requireAuth: yup.mixed<RequireAuth>().oneOf(Object.values(RequireAuth)),
        webExperience: yup.mixed<WebExpOptions>().oneOf(Object.values(WebExpOptions)),
        allowVanillaZoom: yup.boolean().optional(),
        rosterSelfEnrollment: yup.boolean().optional(),
        unauthUserAccess: yup.mixed<UnauthUserAccess>().oneOf(Object.values(UnauthUserAccess)),
        unauthUserText: yup.string().optional(),
        classJoinPermission: yup.mixed<ClassJoinPermission>().oneOf(Object.values(ClassJoinPermission)),
        instructorEmails: yup
            .array()
            .of(yup.string())
            .test({
                test(emails) {
                    if (emails) {
                        const errorEmails: string[] = [];

                        emails.forEach((email) => {
                            if (email && !isValidEmail(email)) {
                                errorEmails.push(email);
                            }
                        });

                        const errorEmailsString = errorEmails.join(', ');

                        if (errorEmails.length) {
                            return this.createError({
                                message: t('advanced.invalid-emails', { errorEmails: errorEmailsString }),
                                path: 'instructorEmails'
                            });
                        }
                    }
                    return true;
                }
            }),
        instructor: yup.string().optional().nullable(),
        contentType: yup.string().optional(),
        status: yup.string().oneOf(['published', 'draft']).optional()
    });

    const mergedSchema = schema.concat(yup.object().shape({ dates: yup.array().of(calendarSchema) }));

    const generatePassword = useCallback(() => {
        const passwordGenerator = new PasswordGenerator();
        const password = passwordGenerator.generatePassword({ length: 10 });
        return password;
    }, []);

    const defaultValues = useMemo<InitialClassFormValues>(
        () => ({
            ...STATIC_DEFAULT_VALUES,
            classPasscode: generatePassword(),
            dates: [defaultValuesWithInitialDay]
        }),
        [defaultValuesWithInitialDay, generatePassword]
    );

    const { control, watch, handleSubmit, register, getValues, reset, formState, setValue } = useForm({
        defaultValues,
        resolver: yupResolver(mergedSchema)
    });

    const formErrors = formState.errors;

    const courseTemplateSelected = watch('courseTemplate');

    useEffect(() => {
        if (!courseTemplateSelected) {
            setValue('courseTemplateId', '');
        }
    }, [setValue, courseTemplateSelected]);

    const setFormValuesFromOrgDefaults = useCallback(() => {
        const defaultsKeys: (keyof OverridableClassOptions)[] = [
            'allowVanillaZoom',
            'webExperience',
            'unauthUserAccess',
            'unauthUserText',
            'classJoinPermission',
            'rosterSelfEnrollment',
            'attendanceGradeTardyScore',
            'hasAiTa',
            'hasFileAnnotations',
            'hasPresentations',
            'allowNextGenMobile',
            'enablePrivateDiscussion',
            'orgScreenshareRes'
        ];

        defaultsKeys.forEach((key) => {
            if (orgSettings?.featureSettings.options[key]) {
                setValue(key, orgSettings?.featureSettings.options[key]);
            }
        });
    }, [orgSettings?.featureSettings.options, setValue]);

    useEffect(() => {
        setFormValuesFromOrgDefaults();
    }, [setValue, orgSettings, setFormValuesFromOrgDefaults]);

    const { append, remove, fields } = useFieldArray({ name: 'dates', control });

    const addDefaultDate = () => {
        append(defaultCalendarValues);
    };

    const removeDate = (index: number) => {
        remove(index);
    };

    const watchDatesArray = watch('dates');

    const dateFields = fields.map((field, index) => ({
        ...field,
        ...watchDatesArray[index]
    }));

    const sessions: ClassSessionWithGroupData[] = useMemo(() => {
        // @NOTE: sessions can be derived from user input in the date fields OR
        // sessions can be derived from what came from the database

        if (formState.dirtyFields?.dates || formActionType === 'CREATE') {
            // active representation of user's intent when filling out date fields
            return calcSessionsFromDates(dateFields);
        }

        if (getClassToEditStatus === 'SUCCESS' && initialClassTimes) {
            // representation of what's in the db
            return calcSessionsFromClassTimes(
                initialClassTimes,
                classRecord?.data?.timezone ?? '',
                originalClassSessionGroups
            );
        }

        return [];
    }, [
        formState.dirtyFields?.dates,
        getClassToEditStatus,
        initialClassTimes,
        formActionType,
        dateFields,
        classRecord?.data?.timezone,
        originalClassSessionGroups
    ]);

    const [excludedSessions, setExcludedSessions] = useState<ClassSession[]>([]);

    const prevSessions = usePrevious(sessions);

    useEffect(() => {
        // if sessions are updated, reset excluded sessions
        // this prevents the need for some kind of reset exclusions button, or having to start the form from scratch
        if (!_.isEqual(sessions, prevSessions)) {
            setExcludedSessions([]);
        }
    }, [sessions, prevSessions]);

    const fillForm = useCallback(
        (data) => {
            const timezone = data?.class?.data?.timezone ?? '';
            const classTimes = data?.class?.data?.class_times ?? '';
            const dates = aggregateCalendarDates({
                classTimes,
                timezone,
                classSessionGroups: data?.classSessionGroups ?? []
            });

            interface DefaultSettingInterface {
                setting: keyof OverridableClassOptions;
            }
            const defaultSetting = ({ setting }: DefaultSettingInterface) => {
                const classOptions = data?.class?.class_options ?? {};
                const orgOptions = orgSettings?.featureSettings?.options ?? {};

                const classLevelSetting = classOptions[setting];
                const orgLevelSetting = orgOptions[setting];

                return classLevelSetting ?? orgLevelSetting ?? STATIC_DEFAULT_VALUES[setting];
            };

            const initial: InitialClassFormValues = {
                ...STATIC_DEFAULT_VALUES,
                className: data?.class?.class_name ?? '',
                classId: data?.class?.class_code ?? '',
                description: data?.class.class_description ?? '',
                classPasscode: data?.meeting?.password ?? '',
                waitingRoom: data?.meeting?.settings?.waiting_room ? WaitingRoom.ON : WaitingRoom.OFF,
                // We want canJoinBeforeInstructor to be YES if join_before_host undefined
                canJoinBeforeInstructor:
                    data?.meeting?.settings?.join_before_host === false
                        ? CanJoinBeforeInstructor.NO
                        : CanJoinBeforeInstructor.YES,
                requireAuth: data?.meeting?.settings?.meeting_authentication ? RequireAuth.ON : RequireAuth.OFF,
                dates,
                classSessions: data?.classSessions ?? [],
                classSessionGroups: data?.classSessionGroups ?? [],
                classGroups: data?.classGroups ?? [],
                contentType: data?.class?.content_type ?? 'lms',
                webExperience: defaultSetting({ setting: 'webExperience' }),
                allowVanillaZoom: defaultSetting({ setting: 'allowVanillaZoom' }),
                unauthUserAccess: defaultSetting({ setting: 'unauthUserAccess' }),
                unauthUserText: defaultSetting({ setting: 'unauthUserText' }),
                classJoinPermission: defaultSetting({ setting: 'classJoinPermission' }),
                rosterSelfEnrollment: defaultSetting({ setting: 'rosterSelfEnrollment' }),
                attendanceGradeTardyScore: defaultSetting({ setting: 'attendanceGradeTardyScore' }),
                hasAiTa: defaultSetting({ setting: 'hasAiTa' }),
                hasFileAnnotations: defaultSetting({ setting: 'hasFileAnnotations' }),
                hasPresentations: defaultSetting({ setting: 'hasPresentations' }),
                allowNextGenMobile: defaultSetting({ setting: 'allowNextGenMobile' }),
                enablePrivateDiscussion: defaultSetting({ setting: 'enablePrivateDiscussion' }),
                classOptions: data?.class?.class_options
            };

            setInitialData(initial);
            reset(initial);
        },
        [orgSettings, reset]
    );

    const parseErrorCode = useCallback(
        (error: any): string => {
            console.error(error);

            const code = error?.response?.data?.code;
            const zoomPasswordError = error?.response?.data?.error;

            if (
                code === CLASS_CREATE_ZOOM_PASSWORD_ERROR &&
                zoomPasswordError &&
                typeof zoomPasswordError === 'string'
            ) {
                return zoomPasswordError;
            }

            return getClassErrorMessage(t, code);
        },
        [t]
    );

    const classNameFocused = useRef(false);

    const handleClassNameFocus = () => {
        classNameFocused.current = true;
    };

    const getUserNameFromToken = useCallback(async () => {
        try {
            const { data } = await axios.get(`${api}${AUTH_ME}`, {
                headers: {
                    user_uuid: userUuid
                }
            });

            if (classNameFocused.current) return;

            // only set the value if it has not been changed by the user
            if (data?.user_name) {
                setValue('className', t('details.default-class-name', { userName: data.user_name }));
            }
        } catch (err) {
            console.error(err);
        }
    }, [api, setValue, t, userUuid]);

    const getPublishedTemplates = useCallback(async (): Promise<any> => {
        if (!userUuid) return;

        try {
            const config: AxiosRequestConfig = {
                headers: {
                    token: userUuid
                },
                params: { status: ClassStatus.Published }
            };

            const { data } = await axios.get<TemplateRecord[]>(`${api}${TEMPLATES_GET}`, config);

            setPublishedTemplates(data);
        } catch (err) {
            console.error('unable to get published templates');
        }
    }, [api, userUuid]);

    useEffect(() => {
        if (publishedTemplates.length === 0 && formActionType === 'CREATE') {
            getPublishedTemplates();
        }
    }, [formActionType, getPublishedTemplates, publishedTemplates.length]);

    useEffect(() => {
        const classNameIsDirty = formState.dirtyFields.className;

        // classNameIsDirty reverts back to undefined when the entire value is cleared.
        // Must use classNameHasBeenSet to prevent the default class name from being set again

        if (classNameIsDirty || classNameHasBeenSet) return;

        if (formActionType === 'CREATE') {
            setClassNameHasBeenSet(true);

            getUserNameFromToken();
        }
    }, [formActionType, formState.dirtyFields.className, classNameHasBeenSet, getUserNameFromToken]);

    const fetchClass = useCallback(async (): Promise<ClassData> => {
        const { data } = await axios.post<ClassData>(
            processApiPath(GET_CLASS_DATA_PATH),
            {
                class_uuid: classUuid,
                with_info: 'true'
            },
            { headers: getUserUuidHeaders(userUuid as string) }
        );

        if ((data as any)?.success === false) {
            throw Error((data as any)?.error);
        }

        if (data && data.classSessions && data.classSessionGroups) {
            const classSessionGroupsByUuid = {};
            data.classSessionGroups.forEach((classSessionGroup) => {
                classSessionGroupsByUuid[classSessionGroup.class_session_group_uuid] = classSessionGroup;
            });
            data.classSessions.forEach((classSession) => {
                if (classSession.class_session_group_uuid) {
                    classSession.class_session_group = classSessionGroupsByUuid[classSession.class_session_group_uuid];
                }
            });
        }
        return data;
    }, [userUuid, classUuid]);

    const getClass = useCallback(async () => {
        setGetClassToEditStatus('LOADING');

        try {
            const data = state.classData ?? (await fetchClass());
            const zoomMeetingData = data?.meeting as ZoomMeetingData;

            setInitialClassTimes(data?.class?.data?.class_times ?? '');
            fillForm(data);
            setClassRecord(data.class);
            setLeadInstructor(data.lead_instructor || null);
            setMeetingSettings(zoomMeetingData?.settings);
            setHostEmail(data.lead_instructor?.email || '');

            setClassGroups(data.classGroups || []);
            setOriginalClassSessionGroups(data.classSessionGroups || []);

            setLtiState(data?.class?.lti_state || {});
            setLmsSyncSettings(data?.class?.lms_sync_settings || {});

            setGetClassToEditStatus('SUCCESS');
        } catch (err) {
            const errorMsg = parseErrorCode(err);
            setErrorMessage(errorMsg);
            setGetClassToEditStatus('ERROR');
        }
    }, [state.classData, fetchClass, fillForm, parseErrorCode]);

    useEffect(() => {
        // Edit existing class
        if (classUuid && userUuid && orgSettings) {
            getClass();
        }
    }, [classUuid, getClass, orgSettings, userUuid]);

    const excludeSession = (session: ClassSession) => {
        setExcludedSessions((previous) => [...previous, session]);
    };

    const buildCreatePayload = useCallback((): ClassCreatePayload => {
        const {
            className,
            classId,
            description,
            dates,
            classPasscode,
            canJoinBeforeInstructor,
            waitingRoom,
            requireAuth,
            courseTemplate,
            courseTemplateId,
            additionalInstructors,
            contentType,
            instructor,
            status,
            webExperience,
            allowVanillaZoom,
            unauthUserAccess,
            unauthUserText,
            classJoinPermission,
            rosterSelfEnrollment,
            attendanceGradeTardyScore,
            hasAiTa,
            hasFileAnnotations,
            hasPresentations,
            allowNextGenMobile,
            enablePrivateDiscussion
        } = getValues();

        const filteredSessions = filterSessions({ sessions, excludedSessions });
        const startAndEnd = filteredSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const meetingObj: MeetingObj = {
            topic: className,
            password: classPasscode,
            agenda: description,
            settings: {
                join_before_host: canJoinBeforeInstructor === CanJoinBeforeInstructor.YES,
                waiting_room: waitingRoom === WaitingRoom.ON,
                meeting_authentication: requireAuth === RequireAuth.ON
            }
        };

        const classSessionGroupsPayloadArray: ClassSessionGroupPayload[] = groupSessions(sessions);

        const classDataNew: ClassDataUpdate = {
            class_name: className,
            class_description: description,
            class_code: classId,
            content_type: contentType
        };

        if (courseTemplate && courseTemplateId) {
            classDataNew.template_id = courseTemplateId;
        }

        if (dates.length) {
            classDataNew.timezone = dates[0]?.timezone ?? null;
            classDataNew.class_times = startAndEnd.join(',');
        }

        const formClassOptions = {
            webExperience,
            unauthUserAccess,
            unauthUserText,
            allowVanillaZoom,
            classJoinPermission,
            rosterSelfEnrollment,
            attendanceGradeTardyScore,
            hasAiTa,
            hasFileAnnotations,
            hasPresentations,
            allowNextGenMobile,
            enablePrivateDiscussion
        };

        const classOptions = returnClassOptions({
            formClassOptions,
            orgOptions: orgSettings?.featureSettings?.options ?? {}
        });

        const payload: ClassCreatePayload = {
            meeting_obj: meetingObj,
            class_data: classDataNew,
            user_uuid: userUuid,
            class_session_groups: classSessionGroupsPayloadArray,
            additional_instructors: additionalInstructors,
            status,
            class_options: classOptions
        };

        if (instructor) {
            payload.instructor = instructor;
        }

        return payload;
    }, [excludedSessions, getValues, orgSettings?.featureSettings.options, sessions, userUuid]);

    const buildEditPayload = useCallback((): ClassUpdatePayload => {
        const formValues = getValues();

        const initialSessions = calcSessionsFromClassTimes(
            initialClassTimes,
            classRecord?.data?.timezone ?? '',
            originalClassSessionGroups
        );
        const initialStartAndEnd = initialSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const filteredSessions = filterSessions({ sessions, excludedSessions });
        const startAndEnd = filteredSessions.map((session) => `${session.start_time}:${session.end_time}`);

        const meetingDelta = returnMeetingDataDelta(initialData, formValues);

        const initialFormData: any = initialData;
        const formData: any = formValues;

        if (initialData && initialData.dates.length) {
            initialFormData.timezone = initialData.dates[0]?.timezone ?? null;
            initialFormData.class_times = initialStartAndEnd.join(',');
        }

        if (formValues.dates.length) {
            formData.timezone = formValues.dates[0]?.timezone ?? null;
            formData.class_times = startAndEnd.join(',');
        }

        const classDataDelta = returnClassDataDelta(initialFormData, formData);

        const overridableOptions: OverridableClassOptions = {
            allowVanillaZoom: formValues.allowVanillaZoom,
            unauthUserAccess: formValues.unauthUserAccess,
            unauthUserText: formValues.unauthUserText,
            webExperience: formValues.webExperience,
            classJoinPermission: formValues.classJoinPermission,
            rosterSelfEnrollment: formValues.rosterSelfEnrollment,
            attendanceGradeTardyScore: formValues.attendanceGradeTardyScore,
            hasAiTa: formValues.hasAiTa,
            hasFileAnnotations: formValues.hasFileAnnotations,
            hasPresentations: formValues.hasPresentations,
            allowNextGenMobile: formValues.allowNextGenMobile,
            enablePrivateDiscussion: formValues.enablePrivateDiscussion
        };

        const classOptions = returnClassOptions({
            formClassOptions: overridableOptions,
            orgOptions: orgSettings?.featureSettings.options ?? {},
            originalClassOptions: initialData?.classOptions ?? {}
        });

        classDataDelta.class_options = classOptions;

        const payload: ClassUpdatePayload = {
            user_uuid: userUuid,
            class_uuid: classUuid
        };

        if (!_.isEmpty(meetingDelta)) {
            payload.meeting_obj = meetingDelta;
        }

        if (!_.isEmpty(classDataDelta)) {
            payload.class_data = classDataDelta;
        }

        if (datesHaveBeenUpdated(formState.dirtyFields)) {
            payload.class_session_groups = groupSessions(sessions);
        }

        if (formState.dirtyFields.instructor) {
            payload.instructor = formValues.instructor;
        }

        return payload;
    }, [
        classRecord?.data?.timezone,
        classUuid,
        excludedSessions,
        formState.dirtyFields,
        getValues,
        initialClassTimes,
        initialData,
        orgSettings?.featureSettings.options,
        originalClassSessionGroups,
        sessions,
        userUuid
    ]);

    const resetDefaultValues = useCallback(() => {
        // Ensures that new classes will have a new passcode, and updated calendar values
        const { defaultValuesWithInitialDay: defaultDates } = returnNewDefaultCalendarValues();

        const newDefaultValues = {
            ...defaultValues,
            classPasscode: generatePassword(),
            dates: [defaultDates]
        };

        reset(newDefaultValues);
        setFormValuesFromOrgDefaults();
    }, [defaultValues, generatePassword, reset, returnNewDefaultCalendarValues, setFormValuesFromOrgDefaults]);

    const onClose = useCallback(() => {
        closeWindow();
        resetDefaultValues();
    }, [closeWindow, resetDefaultValues]);

    const handleSuccess = useCallback(() => {
        onClose();

        sendMessage({
            func: 'call_js_on_labeled_window',
            data: { func: 'refresh', label: 'login_window' }
        });

        sendMessage({ func: 'refresh_parent_window' });
    }, [sendMessage, onClose]);

    const onCancel = useCallback(() => {
        onClose();
        setErrorMessage('');
        setCreateClassStatus('INITIAL');
    }, [onClose]);

    const createClass = useCallback(
        async ({ onSuccess, onError }: ClassRequestCallbacks = {}) => {
            try {
                const payload = buildCreatePayload();
                await axios.post(`${api}${CLASS_CREATE}`, payload);

                if (onSuccess) {
                    onSuccess();
                } else {
                    handleSuccess();
                }
            } catch (err) {
                const errorMsg = parseErrorCode(err);
                setErrorMessage(errorMsg);
                if (onError) {
                    onError(errorMsg);
                }
            }
        },
        [api, buildCreatePayload, handleSuccess, parseErrorCode]
    );

    const createPublication = useCallback(
        async (sendInvites: boolean, { onSuccess, onError }: ClassRequestCallbacks = {}) => {
            try {
                await axios.post(
                    `${api}${CLASS_PUBLISH}`,
                    { class_id: classRecord?.class_uuid, send_invites: sendInvites },
                    { headers: { token: userUuid } }
                );

                if (onSuccess) {
                    onSuccess();
                } else {
                    handleSuccess();
                }
            } catch (err) {
                const errorMsg = parseErrorCode(err);
                setErrorMessage(errorMsg);
                if (onError) {
                    onError(errorMsg);
                }
            }
        },
        [api, classRecord?.class_uuid, handleSuccess, parseErrorCode, userUuid]
    );

    /**
     * Updates a class in any state, published or draft.
     * However, draft classes cannot move from draft to
     * published through this request alone.
     */
    const updateClass = useCallback(
        async ({ onSuccess, onError }: ClassRequestCallbacks = {}) => {
            setUpdateClassStatus('LOADING');

            try {
                const payload = buildEditPayload();

                await axios.put(`${api}${UPDATE_CLASS}`, { ...payload });

                setUpdateClassStatus('SUCCESS');

                if (onSuccess) {
                    onSuccess();
                } else {
                    handleSuccess();
                }
            } catch (err) {
                const errorMsg = parseErrorCode(err);
                setErrorMessage(errorMsg);
                setUpdateClassStatus('ERROR');
                if (onError) {
                    onError(errorMsg);
                }
            }
        },
        [api, handleSuccess, buildEditPayload, parseErrorCode]
    );

    return {
        allowForcedAuthentication,

        excludedSessions,
        excludeSession,

        handleSubmit,
        formActionType,
        register,
        addDefaultDate,
        createPublication,
        createClassStatus,
        handleClassNameFocus,

        createClass,
        updateClass,
        updateClassStatus,
        classRecord,
        getClass,

        classGroups,
        leadInstructor,
        publishedTemplates,

        hostEmail,
        setHostEmail,

        getClassToEditStatus,
        removeDate,
        sessions,
        control,
        reset,
        errorMessage,
        setErrorMessage,
        onCancel,
        dateFields,
        setValue,
        watch,

        ltiState,
        lmsSyncSettings,
        formErrors,
        userUuid,

        resetDefaultValues,
        zoomAppId: orgSettings?.zoomAppId,
        schoolOverrides: orgSettings?.featureSettings?.overrides,

        recordings,
        setRecordings
    };
};

export const ClassContainer = createContainer(useClassContainer);
